/**
 * WebSocket transport for the review workspace.
 *
 * One connection per review, shared across every hook in the tab. The
 * manager owns:
 *   • connect/reconnect with exponential backoff
 *   • token refresh on policy-close (server closes with 1008 when the
 *     JWT expires mid-session)
 *   • fanout to subscribed listeners
 *   • outbound send queue (drops non-critical frames while disconnected;
 *     comments and other user-authored writes are the responsibility of
 *     REST mutations, not this channel)
 *
 * The public consumer is `useReviewLive` in features/review/api. Nothing
 * else should import from here directly — this module is transport, the
 * hook is the contract.
 *
 * Message shapes mirror pyxis/api/src/pyxisrc_ai/ws/hub.py and
 * routes_ws.py. If the backend contract changes, edit the discriminated
 * unions below — the rest of the code will surface the drift at build
 * time.
 */

import { clearAuthSession, readAuthSession, readAuthToken } from "./auth-token"
import { ACCESS_TOKEN_REFRESH_MARGIN_MS, refreshAccessTokenIfPossible } from "./http"

// ── Wire types ────────────────────────────────────────────────────────

/** A frame the client can receive from the server. */
export type InboundFrame =
  // Backend sends `viewers` as a role-only string list (see routes_ws.py
  // `_viewer_label`). Identities are deliberately withheld from the frame —
  // a client channel must not disclose staff names to the counterparty.
  // Presence arrives as a join or a leave, each carrying the full viewer list
  // as it now stands — so a late joiner and a reconnecting client converge
  // with no replay. The bare `presence` variant is kept because a deployment
  // may be running a server that predates the split (routes_ws.py).
  | { type: "presence"; viewers: string[] }
  | { type: "presence.joined"; viewer: string; viewers: string[] }
  | { type: "presence.left"; viewer: string; viewers: string[] }
  // `viewer_name` is present only for internal readers: a client's connection
  // strips it server-side (routes_ws `_ClientConnectionSkippingStaffOnlyFrames`),
  // so a client sees the role alone. Optional here for that reason, and because
  // a service principal has no name.
  | { type: "typing"; viewer: string; viewer_name?: string }
  | { type: "cursor.moved"; cursor: CursorWire }
  | { type: "comment.created"; comment: unknown }
  | { type: "review.updated"; review: unknown }
  | { type: "pong" }
  | { type: "error"; detail: string }

/** A frame the client can send to the server. */
export type OutboundFrame =
  { type: "typing" } | { type: "cursor"; page: number; x: number; y: number } | { type: "ping" }

export interface CursorWire {
  user_id: string
  role: string
  page: number
  x: number
  y: number
  updated_at: number
}

// ── Manager ───────────────────────────────────────────────────────────

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed"

/**
 * Listener callback receives every inbound frame. Return value is
 * ignored; subscribers who care about one frame type filter internally.
 */
export type FrameListener = (frame: InboundFrame) => void
export type StatusListener = (status: ConnectionStatus) => void

/**
 * How long a channel stays open with no consumers.
 *
 * Long enough to span a React remount — StrictMode's deliberate double-invoke,
 * or a route change that re-renders the workspace — and short enough that a
 * reader who navigates away stops counting as present almost at once.
 */
const IDLE_CLOSE_GRACE_MS = 250

const BACKOFF_BASE_MS = 500
const BACKOFF_MAX_MS = 15_000
const HEARTBEAT_INTERVAL_MS = 25_000
const CURSOR_THROTTLE_MS = 50

/** Server close code for JWT expiry. Distinct from user-initiated close. */
/** `routes_ws._REASON_UNAUTHENTICATED` — the only 1008 that means the session. */
const REASON_UNAUTHENTICATED = "Authentication required."

/**
 * Whether a refused socket means the session is dead.
 *
 * There is no 401/403 on a socket, so the server closes every refusal with
 * 1008 and puts the detail in the reason. One of those reasons is a credential
 * it will never accept; the others are about the review — including one that
 * does not exist, which closes with "Review not found."
 *
 * Matched exactly rather than loosely, so a reason added server-side does not
 * silently start signing people out.
 */
export function closeMeansSessionIsDead(reason: string): boolean {
  return reason === REASON_UNAUTHENTICATED
}

const CLOSE_CODE_POLICY_VIOLATION = 1008

interface Channel {
  reviewId: string
  socket: WebSocket | null
  status: ConnectionStatus
  reconnectAttempt: number
  reconnectTimer: ReturnType<typeof setTimeout> | null
  heartbeatTimer: ReturnType<typeof setInterval> | null
  frameListeners: Set<FrameListener>
  statusListeners: Set<StatusListener>
  lastCursorSentAt: number
  closedByUs: boolean
  /** Pending close, cancelled if a consumer re-attaches inside the grace window. */
  idleTimer: ReturnType<typeof setTimeout> | null
  /**
   * An `open` that has begun but not yet assigned a socket.
   *
   * `open` awaits a token before constructing the WebSocket, so `socket` stays
   * null across that await. Without this flag every consumer that attaches in
   * the gap sees no socket and opens one of its own — and the server counts a
   * registration per socket, so one page load reads as several viewers.
   */
  opening: boolean
}

const channels = new Map<string, Channel>()

function ensureChannel(reviewId: string): Channel {
  const existing = channels.get(reviewId)
  if (existing) return existing
  const created: Channel = {
    reviewId,
    socket: null,
    status: "closed",
    reconnectAttempt: 0,
    reconnectTimer: null,
    heartbeatTimer: null,
    frameListeners: new Set(),
    statusListeners: new Set(),
    lastCursorSentAt: 0,
    closedByUs: false,
    opening: false,
    idleTimer: null,
  }
  channels.set(reviewId, created)
  return created
}

function setStatus(channel: Channel, next: ConnectionStatus): void {
  if (channel.status === next) return
  channel.status = next
  channel.statusListeners.forEach((fn) => fn(next))
}

/**
 * The JWT rides in a query string because the browser WebSocket API
 * cannot set request headers. This is the industry-common pattern
 * (GitHub, Slack, Linear all do it) and safe over `wss://`, but the
 * token can appear in server access logs if verbose logging is on.
 *
 * TODO(production hardening): swap to a single-use ticket flow —
 * `POST /api/v1/auth/ws-ticket` returns a ~30s Redis-backed opaque
 * ticket; this connects with `?ticket=…` instead of `?token=…`. Only
 * this function needs to change (plus one HTTP call ahead of connect).
 */
function buildSocketUrl(reviewId: string, token: string): string {
  const proto = globalThis.location.protocol === "https:" ? "wss:" : "ws:"
  const host = globalThis.location.host
  const encodedId = encodeURIComponent(reviewId)
  const encodedToken = encodeURIComponent(token)
  return `${proto}//${host}/api/v1/reviews/${encodedId}/live?token=${encodedToken}`
}

function scheduleReconnect(channel: Channel): void {
  if (channel.reconnectTimer) return
  const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** channel.reconnectAttempt)
  channel.reconnectAttempt += 1
  setStatus(channel, "reconnecting")
  channel.reconnectTimer = setTimeout(() => {
    channel.reconnectTimer = null
    void open(channel)
  }, delay)
}

function startHeartbeat(channel: Channel): void {
  stopHeartbeat(channel)
  channel.heartbeatTimer = setInterval(() => {
    sendRaw(channel, { type: "ping" })
  }, HEARTBEAT_INTERVAL_MS)
}

function stopHeartbeat(channel: Channel): void {
  if (channel.heartbeatTimer) {
    clearInterval(channel.heartbeatTimer)
    channel.heartbeatTimer = null
  }
}

function sendRaw(channel: Channel, frame: OutboundFrame): void {
  const sock = channel.socket
  if (!sock || sock.readyState !== WebSocket.OPEN) return
  try {
    sock.send(JSON.stringify(frame))
  } catch {
    /* socket closing between check and send — the reconnect path handles it */
  }
}

/**
 * Preemptively refresh the access token if it is expired or about to expire.
 * WebSockets have no equivalent of the HTTP 401 replay path — if the token
 * dies between our read and the server's auth check, the handshake closes
 * with 1008 and we reconnect in a loop until either succeeding or losing
 * the refresh token. Refreshing here keeps the connect path deterministic.
 */
async function tokenForConnect(): Promise<string | null> {
  const session = readAuthSession()
  if (!session) return null
  const nearExpiry =
    session.expiresAt > 0 && session.expiresAt < Date.now() + ACCESS_TOKEN_REFRESH_MARGIN_MS
  if (nearExpiry && session.refreshToken) {
    const rotated = await refreshAccessTokenIfPossible()
    if (rotated) return rotated
    // Rotation failed. Fall through to whatever token we still have —
    // the server will close with 1008 if truly stale, and the close
    // handler clears the session for RequireAuth to bounce the user.
  }
  return readAuthToken()
}

async function open(channel: Channel): Promise<void> {
  channel.opening = true
  const token = await tokenForConnect()
  if (!token) {
    channel.opening = false
    // No session — do not attempt to connect. `RequireAuth` will bounce
    // the user to /login; when they return we'll retry via connectReview.
    setStatus(channel, "closed")
    return
  }
  channel.closedByUs = false
  setStatus(channel, channel.reconnectAttempt > 0 ? "reconnecting" : "connecting")

  const socket = new WebSocket(buildSocketUrl(channel.reviewId, token))
  channel.socket = socket
  channel.opening = false

  socket.addEventListener("open", () => {
    channel.reconnectAttempt = 0
    setStatus(channel, "open")
    startHeartbeat(channel)
  })

  socket.addEventListener("message", (event) => {
    const frame = parseFrame(event.data)
    if (!frame) return
    channel.frameListeners.forEach((fn) => fn(frame))
  })

  socket.addEventListener("close", (event) => {
    stopHeartbeat(channel)
    channel.socket = null
    if (channel.closedByUs) {
      setStatus(channel, "closed")
      return
    }
    if (event.code === CLOSE_CODE_POLICY_VIOLATION) {
      /*
         1008 is the server's "you may not do this", and it covers two very
         different situations: a credential it will never accept, and a review
         this principal cannot open — including one that does not exist, which
         closes with "Review not found."

         Only the first is a dead session. Signing someone out because they
         followed a stale link to a deleted review takes their session away over
         somebody else's mistake. There is no 401/403 on a socket, so the server
         puts the detail in the reason (`routes_ws._REASON_*`) and this matches
         the one that means the credential, exactly rather than loosely: a new
         reason added there must not silently start signing people out.

         Either way we stop retrying. Reconnecting cannot make a refused
         credential succeed, and it cannot make a missing review appear.
      */
      if (closeMeansSessionIsDead(event.reason)) clearAuthSession()
      setStatus(channel, "closed")
      return
    }
    scheduleReconnect(channel)
  })

  socket.addEventListener("error", () => {
    // Errors always land in `close` too; letting close drive the state
    // machine keeps one code path.
  })
}

function parseFrame(raw: unknown): InboundFrame | null {
  if (typeof raw !== "string") return null
  try {
    const parsed = JSON.parse(raw) as Partial<InboundFrame> & { type?: string }
    if (!parsed || typeof parsed.type !== "string") return null
    return parsed as InboundFrame
  } catch {
    return null
  }
}

// ── Public API ────────────────────────────────────────────────────────

export interface Connection {
  status: ConnectionStatus
  onFrame(listener: FrameListener): () => void
  onStatus(listener: StatusListener): () => void
  send(frame: OutboundFrame): void
  sendCursor(page: number, x: number, y: number): void
  close(): void
}

/**
 * Open (or attach to) the shared channel for one review. Multiple hooks
 * on the same review share one socket. When the last consumer detaches,
 * the connection is closed.
 */
export function connectReview(reviewId: string): Connection {
  const channel = ensureChannel(reviewId)
  // A consumer arrived: whatever close was pending is no longer wanted.
  if (channel.idleTimer) {
    clearTimeout(channel.idleTimer)
    channel.idleTimer = null
  }
  if (!channel.socket && !channel.opening && channel.status !== "reconnecting") {
    void open(channel)
  }

  let detached = false

  const detachIfLast = () => {
    if (detached) return
    detached = true
    if (channel.frameListeners.size > 0 || channel.statusListeners.size > 0) return
    /*
       Closed after a pause, not immediately.

       React re-runs effects on every remount, and under StrictMode it unmounts
       and remounts once on purpose — so the last consumer detaching is not
       evidence that the reader has gone. Closing on the spot made the remount
       open a second socket, and the server registers presence per socket, so
       one reader appeared twice in the viewer list until the first aged out.
    */
    if (channel.idleTimer) clearTimeout(channel.idleTimer)
    channel.idleTimer = setTimeout(() => {
      channel.idleTimer = null
      if (channel.frameListeners.size === 0 && channel.statusListeners.size === 0) {
        teardown(channel)
      }
    }, IDLE_CLOSE_GRACE_MS)
  }

  return {
    get status() {
      return channel.status
    },
    onFrame(listener) {
      channel.frameListeners.add(listener)
      return () => {
        channel.frameListeners.delete(listener)
        detachIfLast()
      }
    },
    onStatus(listener) {
      channel.statusListeners.add(listener)
      listener(channel.status)
      return () => {
        channel.statusListeners.delete(listener)
        detachIfLast()
      }
    },
    send(frame) {
      sendRaw(channel, frame)
    },
    sendCursor(page, x, y) {
      const now = Date.now()
      if (now - channel.lastCursorSentAt < CURSOR_THROTTLE_MS) return
      channel.lastCursorSentAt = now
      sendRaw(channel, { type: "cursor", page, x, y })
    },
    close() {
      detachIfLast()
    },
  }
}

function teardown(channel: Channel): void {
  channel.closedByUs = true
  channel.opening = false
  if (channel.idleTimer) {
    clearTimeout(channel.idleTimer)
    channel.idleTimer = null
  }
  if (channel.reconnectTimer) {
    clearTimeout(channel.reconnectTimer)
    channel.reconnectTimer = null
  }
  stopHeartbeat(channel)
  const sock = channel.socket
  if (sock && sock.readyState !== WebSocket.CLOSED) {
    try {
      sock.close(1000, "no listeners")
    } catch {
      /* browser sometimes throws on double-close — safe to swallow */
    }
  }
  channel.socket = null
  setStatus(channel, "closed")
  channels.delete(channel.reviewId)
}

/** Test hook: drop all channels. Not exported from the barrel. */
export function __resetForTests(): void {
  channels.forEach((c) => teardown(c))
  channels.clear()
}
